/**
 * Admin portfolio slice (PX-W7.9, TASK-17A2C062) — the org-admin
 * "Projects & people" landing's backing state: the one-request read-all view
 * over every enabled project of the organisation.
 *
 * Keyed by `orgId` exactly like `projectDetailSlice.openProject` (never the
 * legacy unkeyed veto `myWorkSlice` still carries): `fetchStarted`/
 * `fetchSucceeded`/`fetchFailed` all pass `action.meta.arg` (the orgId
 * string, since the thunk's arg IS the key) so a same-org re-dispatch is
 * vetoed while an org change is not, and a superseded org's late reply writes
 * nothing (`px-inflight-and-loaded-keys-must-carry-the-project`).
 *
 * `adminPortfolioOpened` invalidates on every mount (StrictMode-safe: an
 * in-flight load's own settle clears the flag, so the second effect run
 * cannot double-dispatch) rather than the page deciding when to load —
 * mirroring the panel-owns-its-load rule. `adminPortfolioRetried` clears the
 * error and re-arms the same way (`px-retry-needs-an-explicit-invalidate`).
 * There is no close reducer: the page is always mounted at `/admin/projects`
 * for the lifetime of the admin's visit, and a stale view for a superseded
 * org is already cleared at `pending` (`px-inflight-and-loaded-keys-must-
 * carry-the-project`).
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { adminPortfolioService } from '../services/adminPortfolioService.http'
import type { AdminPortfolioView } from '../types'

export interface AdminPortfolioError {
  /**
   * The SERVER's HTTP status, carried verbatim — a 403 must stay
   * distinguishable from a 500 so the page can branch to `ForbiddenPanel`
   * instead of `ErrorPanel` (AC-14).
   */
  status: number
  key?: string
  message: string
}

export interface AdminPortfolioState extends FetchCache {
  view: AdminPortfolioView | null
  error: AdminPortfolioError | null
}

const initialState: AdminPortfolioState = {
  view: null,
  error: null,
  ...initialFetchCache
}

const toAdminPortfolioError = (error: unknown): AdminPortfolioError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

export const fetchAdminPortfolio = createAsyncThunk<
  AdminPortfolioView,
  string,
  { rejectValue: AdminPortfolioError }
>(
  'adminPortfolio/fetch',
  async (orgId, { rejectWithValue }) => {
    try {
      return await adminPortfolioService.get(orgId)
    } catch (error) {
      return rejectWithValue(toAdminPortfolioError(error))
    }
  },
  {
    condition: (orgId, { getState }) =>
      shouldFetch((getState() as { adminPortfolio: AdminPortfolioState }).adminPortfolio, orgId)
  }
)

export const adminPortfolioSlice = createSlice({
  name: 'adminPortfolio',
  initialState,
  reducers: {
    /** Load-on-mount, StrictMode-safe (see the file header). */
    adminPortfolioOpened(state) {
      invalidate(state)
    },
    /** Dismiss the error and re-arm the cache for a fresh attempt. */
    adminPortfolioRetried(state) {
      state.error = null
      invalidate(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminPortfolio.pending, (state, action) => {
        // An org change clears the stale view before the new load lands, so a
        // slow reply for the OLD org can never paint onto the new one.
        if (action.meta.arg !== state.loadedKey) state.view = null
        fetchStarted(state, action.meta.arg)
        state.error = null
      })
      .addCase(fetchAdminPortfolio.fulfilled, (state, action) => {
        if (!fetchSucceeded(state, action.meta.arg)) return
        state.view = action.payload
      })
      .addCase(fetchAdminPortfolio.rejected, (state, action) => {
        if (!fetchFailed(state, action.meta.arg)) return
        state.error = action.payload ?? { status: 0, message: 'unknown' }
      })
  }
})

export const { adminPortfolioOpened, adminPortfolioRetried } = adminPortfolioSlice.actions

interface WithAdminPortfolio {
  adminPortfolio: AdminPortfolioState
}

export const selectAdminPortfolioState = (s: WithAdminPortfolio): AdminPortfolioState =>
  s.adminPortfolio
export const selectAdminPortfolioError = (s: WithAdminPortfolio): AdminPortfolioError | null =>
  s.adminPortfolio.error
export const selectAdminPortfolioLoadedKey = (s: WithAdminPortfolio): string | null =>
  s.adminPortfolio.loadedKey
